import { ApiResult, request, safeRequest } from '@synergy/api';
import { Media } from '@synergy/models';

import { BankAccount } from '../models/bank-accounts';
import { BankAccountErrorCode } from '../../../../models/bank-account-error-code';
import { BankAccountRequest } from './models/bank-account-request';
import { ContactDetailOverviewRequest } from './models/contact-detail-overview-request';
import { EmailErrorCode } from '../../../../models/email-error-code';
import { EmailErrorHandler } from '../../../../api/email-error-handler';
import { EmbeddedContact } from '../../../../../bonus-settlements/models/embedded-contact';
import { OverviewBankAccountErrorHandler } from '../../../../api/overview-bank-account-error-handler';
import { RelatedAccount } from '../models/related-account';

export class ContactOverviewApi {
    constructor(protected baseUrl: string) {}

    public get(contactId: number): Promise<ApiResult<EmbeddedContact>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/contacts/${contactId}/details`),
            response => response.data
        );
    }

    public patchDetail(
        contactId: number,
        changes: ContactDetailOverviewRequest
    ): Promise<ApiResult<EmbeddedContact, EmailErrorCode>> {
        return safeRequest(
            () => request.patch(`${this.baseUrl}/contacts/${contactId}/details`, changes),
            response => response.data,
            EmailErrorHandler
        );
    }

    public createDetail(changes: ContactDetailOverviewRequest): Promise<ApiResult<EmbeddedContact, EmailErrorCode>> {
        return safeRequest(
            () =>
                request.post(`${this.baseUrl}/contacts`, changes, {
                    params: { isSync: true },
                }),
            response => response.data,
            EmailErrorHandler
        );
    }

    public addBankAccount(
        contactId: number,
        bankAccount: BankAccountRequest
    ): Promise<ApiResult<BankAccount, BankAccountErrorCode>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/contacts/${contactId}/add-bank-account`, { ...bankAccount }),
            response => response.data,
            OverviewBankAccountErrorHandler
        );
    }

    public updateBankAccount(
        contactId: number,
        bankAccount: BankAccountRequest,
        id: number
    ): Promise<ApiResult<BankAccount, BankAccountErrorCode>> {
        return safeRequest(
            () =>
                request.patch(`${this.baseUrl}/contacts/${contactId}/update-bank-account/${id}`, {
                    ...bankAccount,
                }),
            response => response.data,
            OverviewBankAccountErrorHandler
        );
    }

    public deleteBankAccount(contactId: number, bankAccountId: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.delete(`${this.baseUrl}/contacts/${contactId}/remove-bank-account/${bankAccountId}`),
            () => null
        );
    }

    public uploadFile(contactId: number, bankAccountId: number, file: File): Promise<ApiResult<Media>> {
        return safeRequest(
            () => {
                const data = new FormData();
                data.append('file', file, file.name);

                const config = { headers: { 'content-type': 'multipart/form-data' } };

                return request.post(
                    `${this.baseUrl}/contacts/${contactId}/upload-bank-account-media/${bankAccountId}`,
                    data,
                    config
                );
            },
            response => response.data
        );
    }

    public uploadProfileImage(contactId: number, file: File): Promise<ApiResult<Media>> {
        return safeRequest(
            () => {
                const data = new FormData();
                data.append('file', file, file.name);

                const config = { headers: { 'content-type': 'multipart/form-data' } };

                return request.post(`${this.baseUrl}/contacts/${contactId}/upload-image`, data, config);
            },
            response => response.data
        );
    }

    public removeProfileImage(contactId: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.delete(`${this.baseUrl}/contacts/${contactId}/remove-image`),
            () => null
        );
    }

    public getRelatedAccounts(contactId: number): Promise<ApiResult<RelatedAccount[]>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/contacts/${contactId}/related-accounts`),
            response => response.data
        );
    }
}
